'use client';

/**
 * AccountMenu: the ONE account menu (std-overlays G14). The header avatar menu (UnifiedHeader `UserMenuDropdown`) and
 * the legacy header's `UserMenu` (components/ui/UserMenu.tsx) were the same menu built twice, each with its own
 * absolute panel, colours, outside-click handler and focus code. Both now render this: the shared DropdownMenu in
 * look="standard" (popup surface, edge, radius, shadow, 30px rows, the layer stack), non-modal like the panels it
 * replaces, so a press outside still lands where it was aimed.
 *
 * Keyboard and focus come from the primitive: Escape and a choice close it and return focus to the trigger; an
 * outside press closes it and leaves focus where the person put it.
 *
 * The callers keep their own items, test ids and data-demo-ids. Feature code adds rows with AccountMenuItem /
 * AccountMenuLinkItem and groups them with AccountMenuSection; it never styles the panel.
 */
import * as React from 'react';
import Link from 'next/link';

import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  MenuOptionContent,
} from '@/components/ui/dropdown-menu';
import type { OptionTone } from '@/components/ui/overlay-recipes';

type IconType = React.ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>;

export interface AccountMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The trigger button (rendered as is; Radix adds the menu-button semantics). */
  trigger: React.ReactElement;
  /** The identity block at the top (who is signed in). Not a menu item. */
  header?: React.ReactNode;
  /** Panel width (layout only): 'sm' 224px, 'md' 256px, 'lg' 288px, the widths the two old panels had. */
  width?: 'sm' | 'md' | 'lg';
  /** The element that holds the trigger (callers keep a ref to it). */
  rootRef?: React.Ref<HTMLDivElement>;
  /** Rendered next to the menu, outside the panel (dialogs opened from an item). */
  after?: React.ReactNode;
  children: React.ReactNode;
}

const WIDTH = { sm: 'w-56', md: 'w-64', lg: 'w-72' } as const;

export function AccountMenu({ open, onOpenChange, trigger, header, width = 'md', rootRef, after, children }: AccountMenuProps) {
  return (
    <div ref={rootRef} className="relative">
      <DropdownMenu look="standard" modal={false} open={open} onOpenChange={onOpenChange}>
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          sideOffset={8}
          data-account-menu=""
          aria-label="Account"
          className={cn(WIDTH[width], 'max-h-[calc(100dvh-5rem)]')}
        >
          {header ? <div className="px-2.5 pt-1.5 pb-2">{header}</div> : null}
          {children}
        </DropdownMenuContent>
      </DropdownMenu>
      {after}
    </div>
  );
}

/** A group of rows, with a separator above it unless it is the first, and an optional group label. */
export function AccountMenuSection({ label, separator = true, children }: { label?: React.ReactNode; separator?: boolean; children: React.ReactNode }) {
  return (
    <>
      {separator ? <DropdownMenuSeparator /> : null}
      <DropdownMenuGroup>
        {label ? <DropdownMenuLabel>{label}</DropdownMenuLabel> : null}
        {children}
      </DropdownMenuGroup>
    </>
  );
}

/** The signed-in person's initials tile in the menu header (the avatar the panel always showed). */
export function AccountMenuAvatar({ initials, children }: { initials?: React.ReactNode; children?: React.ReactNode }) {
  return (
    <span
      data-account-avatar=""
      aria-hidden="true"
      className="flex size-10 shrink-0 items-center justify-center rounded-item bg-brand-solid text-body font-semibold text-ink"
    >
      {children ?? initials}
    </span>
  );
}

interface RowProps {
  icon?: IconType | null;
  label: React.ReactNode;
  description?: React.ReactNode;
  /** The icon's colour, on a tinted tile (the menu's colour-coded rows). */
  iconTone?: OptionTone;
  /** An active mode (masquerading, simulating): the label in the icon's tone, marked data-active. */
  active?: boolean;
}

/** `tone` (the whole row's colour) also colours the icon; an explicit iconTone puts the icon on its tinted tile. */
function RowContent({ icon, label, description, iconTone, active, tone }: RowProps & { tone?: OptionTone }) {
  return (
    <MenuOptionContent
      icon={icon}
      iconTone={iconTone ?? tone}
      iconTile={iconTone !== undefined}
      description={description}
      labelTone={active ? iconTone ?? 'brand' : undefined}
    >
      {label}
    </MenuOptionContent>
  );
}

export interface AccountMenuItemProps extends RowProps {
  onSelect?: (event: Event) => void;
  /** The whole row's text colour (a highlighted action such as "New call", or sign out). */
  tone?: OptionTone;
  'data-demo-id'?: string;
  'data-testid'?: string;
}

/** An action row (a button). */
export function AccountMenuItem({ icon, label, description, iconTone, active, tone, onSelect, ...data }: AccountMenuItemProps) {
  return (
    <DropdownMenuItem
      onSelect={onSelect}
      tone={tone}
      data-active={active ? '' : undefined}
      className={description ? 'py-1.5' : undefined}
      {...data}
    >
      <RowContent icon={icon} label={label} description={description} iconTone={iconTone} active={active} tone={tone} />
    </DropdownMenuItem>
  );
}

export interface AccountMenuLinkItemProps extends Omit<AccountMenuItemProps, 'onSelect'> {
  href: string;
  /** Opens in a new tab (an external link). */
  external?: boolean;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

/** A navigation row (a link). */
export function AccountMenuLinkItem({ icon, label, description, iconTone, active, tone, href, external, onClick, ...data }: AccountMenuLinkItemProps) {
  const content = <RowContent icon={icon} label={label} description={description} iconTone={iconTone} active={active} tone={tone} />;
  return (
    <DropdownMenuItem asChild tone={tone} data-active={active ? '' : undefined} className={description ? 'py-1.5' : undefined} {...data}>
      {external ? (
        <a href={href} target="_blank" rel="noopener noreferrer" onClick={onClick}>
          {content}
        </a>
      ) : (
        <Link href={href} onClick={onClick}>
          {content}
        </Link>
      )}
    </DropdownMenuItem>
  );
}

/**
 * A row that submits a form (sign out). The menu stays open while the form posts (the page navigates away), so the
 * form is still in the document when the browser submits it.
 */
export function AccountMenuSubmitItem({ icon, label, iconTone, tone, ...data }: Omit<AccountMenuItemProps, 'onSelect' | 'description' | 'active'>) {
  return (
    <DropdownMenuItem asChild tone={tone} onSelect={(event) => event.preventDefault()} {...data}>
      <button type="submit" className="w-full text-left">
        <RowContent icon={icon} label={label} iconTone={iconTone} tone={tone} />
      </button>
    </DropdownMenuItem>
  );
}
